import { EMPTY_ADDRESS_FORM } from '~/constants';
import { logger } from '~/utils/logger';
import { TrainingFormValues } from './types';

export const academicsLogger = logger.child('Academics');

export const MOCK_INSTITUTIONS = [
  {
    id: '01',
    name: 'Community College of the Air Force',
    address: {
      addressLine1: '130 W Maxwell Blvd',
      addressLine2: '',
      city: 'Montgomery',
      stateCode: 'AL',
      postalCode: '36112',
      countryCode: 'US',
    },
  },
];

export const emptyInstitution = {
  name: { value: '', label: '' },
  addressLine1: '',
  addressLine2: '',
  city: '',
  postalCode: '',
  state: { label: '', value: '' },
  country: { label: '', value: '' },
};

export const DEGREE_OPTIONS = [
  { label: 'Associate of Arts', value: 'AA' },
  { label: 'Associate of Science', value: 'AS' },
  { label: 'Associate of Applied Science', value: 'AAS' },
  { label: 'Associate of Fine Arts', value: 'AFA' },
  { label: 'Associate of Engineering', value: 'AE' },
  { label: 'Associate of Business Administration', value: 'ABA' },
  { label: 'Associate Degree in Nursing', value: 'ADN' },
  { label: 'Bachelor of Arts', value: 'BA' },
  { label: 'Bachelor of Science', value: 'BS' },
  { label: 'Bachelor of Fine Arts', value: 'BFA' },
  { label: 'Bachelor of Business Administration', value: 'BBA' },
  { label: 'Bachelor of Architecture', value: 'BArch' },
  { label: 'Bachelor of Engineering', value: 'BEng' },
  { label: 'Bachelor of Technology', value: 'BTech' },
  { label: 'Bachelor of Laws', value: 'LLB' },
  { label: 'Bachelor of Medicine, Bachelor of Surgery', value: 'MBBS' },
  { label: 'Bachelor of Music', value: 'BM' },
  { label: 'Bachelor of Pharmacy', value: 'BPharm' },
  { label: 'Bachelor of Social Work', value: 'BSW' },
  { label: 'Bachelor of Theology', value: 'BTh' },
  { label: 'Master of Business Administration (MBA)', value: 'MBA' },
  { label: 'Master of Science in Nursing', value: 'MSN' },
  { label: 'Master of Science in Nursing (Nurse Practitioner Track)', value: 'NP-MSN' },
  { label: 'Doctor of Nursing Practice (Nurse Practitioner Track)', value: 'NP-DNP' },
  { label: 'Master of Physician Assistant Studies', value: 'MPAS' },
  { label: 'Doctor of Nursing Practice', value: 'DNP' },
  { label: 'Doctor of Medicine', value: 'MD' },
  { label: 'Doctor of Osteopathic Medicine', value: 'DO' },
  { label: 'Doctor of Dental Surgery', value: 'DDS' },
  { label: 'Doctor of Dental Medicine', value: 'DMD' },
  { label: 'Doctor of Physical Therapy', value: 'DPT' },
  { label: 'Doctor of Pharmacy', value: 'PharmD' },
  { label: 'Doctor of Veterinary Medicine', value: 'DVM' },
  { label: 'Doctor of Education', value: 'EdD' },
  { label: 'Doctor of Philosophy', value: 'PhD' },
  { label: 'Juris Doctor', value: 'JD' },
  { label: 'Associate of Applied Science in Radiologic Technology', value: 'AAS-RT' },
  { label: 'Bachelor of Science in Radiologic Technology', value: 'BS-RadTech' },
  { label: 'Associate Degree in Respiratory Therapy', value: 'AAS-RespTherapy' },
  { label: 'Bachelor of Science in Respiratory Therapy', value: 'BS-RespTherapy' },
  { label: 'Associate of Applied Science in Medical Laboratory Technology', value: 'AAS-MLT' },
  { label: 'Bachelor of Science in Medical Laboratory Science', value: 'BS-MLS' },
  { label: 'Associate of Applied Science in Allied Health', value: 'AAS-HA' },
  { label: 'Bachelor of Science in Allied Health', value: 'BS-HA' },
  { label: 'Bachelor of Health Science', value: 'BHS' },
  { label: 'Master of Health Administration', value: 'MHA' },
  { label: 'Master of Science in Health Administration', value: 'MS-HA' },
  { label: 'Bachelor of Science in Health Information Management', value: 'BS-HIM' },
  { label: 'Associate of Applied Science in Health Information Technology', value: 'AAS-HIT' },
];

export const emptyEducationForm = {
  id: -1,
  startDate: '',
  endDate: '',
  graduatedFlag: false,
  graduationDate: null,
  institution: emptyInstitution,
  degree: DEGREE_OPTIONS[0],
  fieldOfStudy: '',
  explanationTx: '',
  details: [],
  validFlag: true,
  warnings: {},
};

// MCV-694: use this factory anywhere we "reset" the education form context.
// Assigning the shared `emptyEducationForm` const repeatedly kept the reference
// stable, so the `useEffect([form])` in EducationForm.tsx never fired on Add
// after a prior save and the previous entry's `details` carried over. Each call
// here produces a fresh object tree so reference-comparison catches the change.
export const createEmptyEducationForm = () => ({
  ...emptyEducationForm,
  institution: {
    ...emptyInstitution,
    name: { ...emptyInstitution.name },
    state: { ...emptyInstitution.state },
    country: { ...emptyInstitution.country },
  },
  details: [],
  warnings: {},
});

// Keep this list in lockstep with `DetailFieldLabelType` in `~/types/mcv/education.ts`.
export const DETAIL_LABELS_OPTIONS = [
  'Honors',
  'Awards',
  'Information',
  'Class Details',
  'Grades',
].map((item) => ({ label: item, value: item }));
export const EMPTY_OPTION = { label: '', value: '' };
export const EMPTY_OPTION_WITH_ID = { label: '', value: '', id: undefined };
export const TRAINING_TYPES = ['Internship', 'Residency', 'Fellowship', 'Clinical Rotation'].map(
  (item) => ({ label: item, value: item })
);

export const emptyDetailsForm = {
  fieldLabel: EMPTY_OPTION,
  fieldTxt: '',
};

export const emptyTrainingForm: Omit<TrainingFormValues, 'id'> = {
  trainingType: TRAINING_TYPES[0],
  facility: {
    label: '',
    value: '',
    userAdded: false,
    address: EMPTY_ADDRESS_FORM,
  },
  medicalBoard: EMPTY_OPTION_WITH_ID,
  specialtyBoard: EMPTY_OPTION_WITH_ID,
  specialtyCertification: EMPTY_OPTION,
  subSpecialtyCertification: EMPTY_OPTION,
  startDate: '',
  endDate: '',
  completedFlag: false,
  completionDate: '',
  explanationTx: '',
  validFlag: true,
  warnings: {},
};

export const createEmptyTrainingForm = (): Omit<TrainingFormValues, 'id'> => ({
  ...emptyTrainingForm,
  trainingType: { ...emptyTrainingForm.trainingType },
  facility: {
    ...emptyTrainingForm.facility,
    address: {
      ...emptyTrainingForm.facility.address,
      addressType: {
        label: emptyTrainingForm.facility.address.addressType?.label ?? '',
        value: emptyTrainingForm.facility.address.addressType?.value ?? '',
      },
      stateCode: { ...emptyTrainingForm.facility.address.stateCode },
      countryCode: { ...emptyTrainingForm.facility.address.countryCode },
    },
  },
  medicalBoard: { ...emptyTrainingForm.medicalBoard },
  specialtyBoard: { ...emptyTrainingForm.specialtyBoard },
  specialtyCertification: { ...emptyTrainingForm.specialtyCertification },
  subSpecialtyCertification: {
    label: emptyTrainingForm.subSpecialtyCertification?.label ?? '',
    value: emptyTrainingForm.subSpecialtyCertification?.value ?? '',
  },
  warnings: {},
});
